import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, Lock, Eye, RefreshCcw, Archive, CheckCircle2, MessageSquare, StickyNote, Save } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "mockup_gallery_token";

const STATUS_STYLES = {
  new: { bg: "#0ea5e9", label: "NEW" },
  reviewed: { bg: "#8b5cf6", label: "REVIEWED" },
  quoted: { bg: "#059669", label: "QUOTED" },
  archived: { bg: "#6b7280", label: "ARCHIVED" },
};

function TokenGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      // ping list endpoint to verify
      await api.get("/admin/design-previews", { params: { token: value.trim(), limit: 1 } });
      sessionStorage.setItem(STORAGE_KEY, value.trim());
      onUnlock(value.trim());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid token");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="mockup-gallery-gate">
      <form
        onSubmit={submit}
        className="w-full max-w-md border-2 border-black bg-white p-8"
        style={{ boxShadow: "8px 8px 0 0 var(--dtf-blue)" }}
      >
        <div className="w-10 h-10 border border-black inline-flex items-center justify-center bg-black text-white">
          <Lock size={16} />
        </div>
        <p className="label-mono mt-4">PRIVATE</p>
        <h1 className="font-display text-4xl leading-[0.9] mt-2">
          MOCKUP<br />GALLERY.
        </h1>
        <p className="font-body text-[color:var(--muted-text)] mt-3 text-sm">
          Enter the admin token to browse recent design-preview submissions from
          the customizer.
        </p>
        <label className="label-mono block mt-6 mb-2" htmlFor="mg-token">Admin Token</label>
        <input
          id="mg-token"
          type="password"
          className="brutal-input w-full"
          placeholder="•••••••••••••"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          data-testid="mockup-gallery-token-input"
        />
        <button
          type="submit"
          disabled={busy}
          className="btn-brutal mt-4 w-full justify-center"
          style={{ background: "var(--dtf-blue)", color: "#fff", borderColor: "#000" }}
          data-testid="mockup-gallery-unlock"
        >
          {busy ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : "Unlock Gallery"}
        </button>
      </form>
    </div>
  );
}

export default function MockupGallery() {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/design-previews", { params: { token, limit: 200 } });
      setRequests(data.requests || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setToken("");
        toast.error("Session expired. Enter the token again.");
      } else {
        toast.error("Couldn't load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchList();
  }, [token, fetchList]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/design-previews/${id}`, { params: { token } });
      setSelected(data);
    } catch (err) {
      toast.error("Couldn't load mockup.");
    } finally {
      setDetailLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/design-previews/${id}`, null, { params: { token, status } });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  useEffect(() => {
    setNotesDraft(selected?.internal_notes || "");
  }, [selected?.id, selected?.internal_notes]);

  const saveNotes = async () => {
    if (!selected) return;
    setNotesSaving(true);
    try {
      await api.patch(`/admin/design-previews/${selected.id}`, null, {
        params: { token, internal_notes: notesDraft },
      });
      const stamp = new Date().toISOString();
      setSelected({ ...selected, internal_notes: notesDraft, internal_notes_updated_at: stamp });
      setRequests((rs) =>
        rs.map((r) =>
          r.id === selected.id
            ? { ...r, internal_notes: notesDraft, internal_notes_updated_at: stamp }
            : r,
        ),
      );
      toast.success("Notes saved.");
    } catch (err) {
      toast.error("Couldn't save notes.");
    } finally {
      setNotesSaving(false);
    }
  };

  const filtered = statusFilter === "all"
    ? requests
    : requests.filter((r) => r.status === statusFilter);

  if (!token) return <TokenGate onUnlock={setToken} />;

  return (
    <div data-testid="mockup-gallery-page">
      <section className="border-b-2 border-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <p className="label-mono text-[color:var(--muted-text)]">ADMIN / DESIGN PREVIEWS</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-3">
            <h1 className="font-display text-5xl md:text-7xl leading-[0.9]">
              MOCKUP GALLERY.
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <span className="label-mono text-[color:var(--muted-text)]">
                {requests.length} total
              </span>
              <button
                onClick={fetchList}
                className="btn-brutal btn-brutal-outline"
                data-testid="mockup-gallery-refresh"
              >
                <RefreshCcw size={14} /> Refresh
              </button>
              <button
                onClick={() => {
                  sessionStorage.removeItem(STORAGE_KEY);
                  setToken("");
                }}
                className="label-mono border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors"
                data-testid="mockup-gallery-signout"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        {/* Status filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="mockup-gallery-filters">
          {["all", "new", "reviewed", "quoted", "archived"].map((s) => {
            const isActive = statusFilter === s;
            const style = STATUS_STYLES[s] || { bg: "#111", label: "ALL" };
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className="label-mono px-3 py-2 border border-black"
                style={
                  isActive
                    ? { background: s === "all" ? "#111" : style.bg, color: "#fff" }
                    : { background: "transparent", color: "#111" }
                }
                data-testid={`mockup-filter-${s}`}
              >
                {s.toUpperCase()}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 label-mono">
            <Loader2 className="animate-spin" size={16} /> Loading submissions…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-black p-10 text-center">
            <p className="font-mono text-sm font-bold">Nothing here.</p>
            <p className="label-mono text-[color:var(--muted-text)] mt-2">
              {statusFilter === "all"
                ? "No design previews submitted yet."
                : `No entries with status "${statusFilter}".`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => {
              const style = STATUS_STYLES[r.status] || STATUS_STYLES.new;
              return (
                <button
                  key={r.id}
                  onClick={() => openDetail(r.id)}
                  className="text-left border-2 border-black bg-white p-4 hover:bg-[color:var(--muted)]"
                  style={{ boxShadow: "4px 4px 0 0 #000" }}
                  data-testid={`mockup-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="label-mono text-[color:var(--muted-text)]">
                        {new Date(r.created_at).toLocaleString()}
                      </p>
                      <p className="font-mono text-sm font-bold mt-1 truncate">
                        {r.customer_name || "Anonymous"}
                      </p>
                      <p className="font-mono text-xs text-[color:var(--muted-text)] truncate">
                        {r.customer_email}
                      </p>
                    </div>
                    <span
                      className="label-mono text-[9px] px-2 py-1 text-white shrink-0"
                      style={{ background: style.bg }}
                    >
                      {style.label}
                    </span>
                  </div>
                  <div className="mt-3 border-t border-black pt-3">
                    <p className="label-mono text-[color:var(--muted-text)]">Product</p>
                    <p className="font-mono text-sm truncate">{r.product_name}</p>
                    <p className="label-mono mt-1 text-[color:var(--muted-text)]">
                      {r.brand} · qty {r.quantity} · {(r.placements || []).length} placement{(r.placements || []).length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <p className="label-mono text-[10px] mt-3 inline-flex items-center gap-1">
                    <Eye size={12} /> View mockup
                    {r.internal_notes && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[color:var(--dtf-blue)]">
                        <StickyNote size={11} /> notes
                      </span>
                    )}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Detail modal */}
      {(selected || detailLoading) && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="mockup-detail-overlay"
        >
          <div
            className="bg-white border-2 border-black max-w-4xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "10px 10px 0 0 var(--dtf-blue)" }}
          >
            {detailLoading || !selected ? (
              <div className="p-10 flex items-center justify-center gap-2 label-mono">
                <Loader2 className="animate-spin" size={16} /> Loading mockup…
              </div>
            ) : (
              <>
                <div className="p-5 border-b-2 border-black flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="label-mono text-[color:var(--muted-text)]">
                      {new Date(selected.created_at).toLocaleString()}
                    </p>
                    <p className="font-display text-2xl mt-1 truncate">
                      {selected.product_name}
                    </p>
                    <p className="font-mono text-sm">
                      {selected.customer_name || "Anonymous"} ·{" "}
                      <a
                        href={`mailto:${selected.customer_email}`}
                        className="underline hover:text-[color:var(--dtf-blue)]"
                      >
                        {selected.customer_email}
                      </a>
                    </p>
                  </div>
                  <button
                    onClick={() => setSelected(null)}
                    className="label-mono px-3 py-2 border border-black hover:bg-black hover:text-white"
                    data-testid="mockup-detail-close"
                  >
                    Close
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                  <div className="md:col-span-3 border-b-2 md:border-b-0 md:border-r-2 border-black bg-[color:var(--muted)] p-4 flex items-center justify-center">
                    <img
                      src={selected.mockup_data_url}
                      alt="Design mockup"
                      className="max-w-full max-h-[70vh] object-contain border border-black bg-white"
                      data-testid="mockup-detail-image"
                    />
                  </div>
                  <div className="md:col-span-2 p-5 space-y-4">
                    <div>
                      <p className="label-mono text-[color:var(--muted-text)]">Brand</p>
                      <p className="font-mono text-sm">{selected.brand}</p>
                    </div>
                    <div>
                      <p className="label-mono text-[color:var(--muted-text)]">Quantity</p>
                      <p className="font-mono text-sm">{selected.quantity}</p>
                    </div>
                    {selected.notes && (
                      <div>
                        <p className="label-mono text-[color:var(--muted-text)] flex items-center gap-1">
                          <MessageSquare size={11} /> Notes
                        </p>
                        <p className="font-body text-sm">{selected.notes}</p>
                      </div>
                    )}
                    {(selected.placements || []).length > 0 && (
                      <div>
                        <p className="label-mono text-[color:var(--muted-text)]">
                          Placements
                        </p>
                        <ul className="space-y-1 mt-1">
                          {selected.placements.map((pl, i) => (
                            <li
                              key={i}
                              className="border border-black px-2 py-1 font-mono text-xs"
                            >
                              {pl.location.replace(/-/g, " ")}
                              {pl.size_inches ? ` · ${pl.size_inches}"` : ""}
                              {pl.artwork_url ? (
                                <>
                                  {" · "}
                                  <a
                                    href={pl.artwork_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline"
                                  >
                                    art
                                  </a>
                                </>
                              ) : (
                                ""
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="border-t-2 border-black pt-4">
                      <p className="label-mono text-[color:var(--muted-text)] mb-2 flex items-center gap-1">
                        <StickyNote size={11} /> Internal Notes
                      </p>
                      <textarea
                        rows={4}
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        className="brutal-input w-full"
                        placeholder="Pricing thoughts, art tweaks needed, follow-up date..."
                        data-testid="mockup-notes-textarea"
                      />
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <span className="label-mono text-[10px] text-[color:var(--muted-text)]">
                          {selected.internal_notes_updated_at
                            ? `Last saved ${new Date(selected.internal_notes_updated_at).toLocaleString()}`
                            : "Not saved yet"}
                        </span>
                        <button
                          type="button"
                          onClick={saveNotes}
                          disabled={notesSaving || notesDraft === (selected.internal_notes || "")}
                          className="btn-brutal"
                          style={{ background: "var(--dtf-blue)", color: "#fff", borderColor: "#000" }}
                          data-testid="mockup-notes-save"
                        >
                          {notesSaving ? (
                            <>
                              <Loader2 size={12} className="animate-spin" /> Saving
                            </>
                          ) : (
                            <>
                              <Save size={12} /> Save Notes
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="border-t-2 border-black pt-4">
                      <p className="label-mono text-[color:var(--muted-text)] mb-2">
                        Set status
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {["new", "reviewed", "quoted", "archived"].map((s) => {
                          const style = STATUS_STYLES[s];
                          const isActive = selected.status === s;
                          return (
                            <button
                              key={s}
                              onClick={() => setStatus(selected.id, s)}
                              className="label-mono px-3 py-2 border border-black inline-flex items-center gap-1"
                              style={
                                isActive
                                  ? { background: style.bg, color: "#fff" }
                                  : { background: "transparent", color: "#111" }
                              }
                              data-testid={`mockup-status-${s}`}
                            >
                              {s === "quoted" && <CheckCircle2 size={11} />}
                              {s === "archived" && <Archive size={11} />}
                              {style.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
